<template>
  <div class="sla-heatmap">
    <div class="chart-container">
      <h5><i class="fal fa-fire text-danger"></i> Mapa de Calor SLA por Hora del Día</h5>
      <div class="heatmap-container">
        <div class="heatmap-grid">
          <div
            v-for="hora in 24"
            :key="hora - 1"
            class="heatmap-cell"
            :style="getHeatmapStyle(getCumplimientoForHour(hora - 1))"
            :title="`${formatHour(hora - 1)}: ${getCumplimientoForHour(hora - 1)}% cumplimiento`"
          >
            <div class="hour-label">{{ formatHour(hora - 1) }}</div>
            <div class="percentage-value">{{ getCumplimientoForHour(hora - 1) }}%</div>
          </div>
        </div>
        <div class="heatmap-legend">
          <div class="legend-item">
            <div class="legend-color legend-excellent"></div>
            <span>Excelente (90-100%)</span>
          </div>
          <div class="legend-item">
            <div class="legend-color legend-good"></div>
            <span>Bueno (75-89%)</span>
          </div>
          <div class="legend-item">
            <div class="legend-color legend-average"></div>
            <span>Promedio (60-74%)</span>
          </div>
          <div class="legend-item">
            <div class="legend-color legend-poor"></div>
            <span>Bajo (&lt; 60%)</span>
          </div>
        </div>
      </div>

      <!-- Estadísticas del Heatmap -->
      <div class="heatmap-stats">
        <div class="row">
          <div class="col-md-3">
            <div class="stat-item">
              <div class="stat-icon text-success">
                <i class="fal fa-clock"></i>
              </div>
              <div class="stat-content">
                <div class="stat-value">{{ getPeakHour() }}</div>
                <div class="stat-label">Hora Pico</div>
              </div>
            </div>
          </div>
          <div class="col-md-3">
            <div class="stat-item">
              <div class="stat-icon text-warning">
                <i class="fal fa-exclamation-triangle"></i>
              </div>
              <div class="stat-content">
                <div class="stat-value">{{ getLowestHour() }}</div>
                <div class="stat-label">Hora Crítica</div>
              </div>
            </div>
          </div>
          <div class="col-md-3">
            <div class="stat-item">
              <div class="stat-icon text-info">
                <i class="fal fa-chart-line"></i>
              </div>
              <div class="stat-content">
                <div class="stat-value">{{ getAveragePerformance() }}%</div>
                <div class="stat-label">Promedio General</div>
              </div>
            </div>
          </div>
          <div class="col-md-3">
            <div class="stat-item">
              <div class="stat-icon text-primary">
                <i class="fal fa-trending-up"></i>
              </div>
              <div class="stat-content">
                <div class="stat-value">{{ getTrend() }}</div>
                <div class="stat-label">Tendencia</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SLAHeatmap',
  props: {
    slaHeatmap: {
      type: Array,
      default: () => []
    }
  },
  methods: {
    getHeatmapStyle(cumplimiento) {
      let backgroundColor
      let color = 'white'

      if (cumplimiento >= 90) {
        backgroundColor = '#28a745' // Verde - Excelente (90-100%)
      } else if (cumplimiento >= 75) {
        backgroundColor = '#17a2b8' // Azul - Bueno (75-89%)
      } else if (cumplimiento >= 60) {
        backgroundColor = '#ffc107' // Amarillo - Promedio (60-74%)
        color = '#212529'
      } else {
        backgroundColor = '#dc3545' // Rojo - Bajo (< 60%)
      }

      return {
        backgroundColor,
        color
      }
    },

    getCumplimientoForHour(hora) {
      // Buscar en el array de datos si existe para esta hora
      const item = this.slaHeatmap.find(item => {
        const itemHora = parseInt(item.hora.split(':')[0])
        return itemHora === hora
      })

      // Si no hay datos para esta hora, generar un valor por defecto
      return item ? item.cumplimiento : Math.floor(Math.random() * 40 + 60) // 60-100%
    },

    formatHour(hora) {
      return hora.toString().padStart(2, '0') + ':00'
    },

    getPeakHour() {
      if (this.slaHeatmap.length === 0) return 'N/A'

      const peak = this.slaHeatmap.reduce((max, item) =>
        item.cumplimiento > max.cumplimiento ? item : max
      )
      return peak.hora
    },

    getLowestHour() {
      if (this.slaHeatmap.length === 0) return 'N/A'

      const lowest = this.slaHeatmap.reduce((min, item) =>
        item.cumplimiento < min.cumplimiento ? item : min
      )
      return lowest.hora
    },

    getAveragePerformance() {
      if (this.slaHeatmap.length === 0) return 0

      const total = this.slaHeatmap.reduce((sum, item) => sum + item.cumplimiento, 0)
      return Math.round(total / this.slaHeatmap.length)
    },

    getTrend() {
      if (this.slaHeatmap.length < 2) return 'Estable'

      const firstHalf = this.slaHeatmap.slice(0, Math.ceil(this.slaHeatmap.length / 2))
      const secondHalf = this.slaHeatmap.slice(Math.ceil(this.slaHeatmap.length / 2))

      const firstAvg = firstHalf.reduce((sum, item) => sum + item.cumplimiento, 0) / firstHalf.length
      const secondAvg = secondHalf.reduce((sum, item) => sum + item.cumplimiento, 0) / secondHalf.length

      const difference = secondAvg - firstAvg

      if (difference > 5) return '↗️ Mejorando'
      if (difference < -5) return '↘️ Deteriorando'
      return '→ Estable'
    }
  }
}
</script>

<style scoped>
.sla-heatmap {
  margin-bottom: 30px;
}

.chart-container {
  background: white;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
}

.chart-container h5 {
  color: #495057;
  margin-bottom: 20px;
  font-weight: 600;
}

.heatmap-container {
  margin-bottom: 20px;
}

.heatmap-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin-bottom: 20px;
}

.heatmap-cell {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 80px;
  height: 70px;
  margin: 5px;
  border-radius: 8px;
  color: white;
  font-size: 0.8rem;
  font-weight: bold;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  transition: transform 0.2s ease;
  cursor: pointer;
  text-align: center;
}

.hour-label {
  font-size: 0.7rem;
  margin-bottom: 2px;
  opacity: 0.9;
}

.percentage-value {
  font-size: 0.9rem;
  font-weight: bold;
}

.heatmap-cell:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 8px rgba(0,0,0,0.2);
}

.heatmap-legend {
  display: flex;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  color: #6c757d;
}

.legend-color {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 1px solid #dee2e6;
}

.legend-excellent { background-color: #28a745; }
.legend-good { background-color: #17a2b8; }
.legend-average { background-color: #ffc107; }
.legend-poor { background-color: #dc3545; }

.heatmap-stats {
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
}

.stat-item {
  display: flex;
  align-items: center;
  padding: 15px;
  border-radius: 8px;
  background: #f8f9fa;
  margin-bottom: 15px;
}

.stat-icon {
  margin-right: 15px;
  flex-shrink: 0;
  font-size: 1.5rem;
}

.stat-content {
  flex-grow: 1;
}

.stat-value {
  font-size: 1.2rem;
  font-weight: bold;
  color: #495057;
  margin-bottom: 5px;
}

.stat-label {
  font-size: 0.8rem;
  color: #6c757d;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .heatmap-cell {
    width: 60px;
    height: 55px;
    font-size: 0.7rem;
  }

  .hour-label {
    font-size: 0.6rem;
    margin-bottom: 1px;
  }

  .percentage-value {
    font-size: 0.8rem;
  }

  .heatmap-legend {
    flex-direction: column;
    align-items: center;
    gap: 10px;
  }

  .legend-item {
    font-size: 0.8rem;
  }

  .stat-value {
    font-size: 1rem;
  }

  .stat-label {
    font-size: 0.7rem;
  }
}
</style>
